import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import {
    Delete,
    Download,
    ExpandLess,
    ExpandMore,
    InfoOutlined,
    Restore,
    Upload
} from '@mui/icons-material';

import { Block, Popup } from '../CommonComponents';
import { useSelector } from '../../redux/hooks';
import { selectUserProfile } from '../../redux/actions/users';
import { formatDateTime } from '../../utils/parsing';
import toastMessage from '../../utils/toast';
import { COLOR_WHITEISH } from '../../theme';
import { prepareOrderFile } from '../../utils/prepare-order-file';
import { s3Upload } from '../../api/s3-upload';
import {
    ORDER_FILE_ACCEPT,
    OrderFile,
    confirmOrderFile,
    deleteOrderFileGroup,
    downloadOrderFile,
    listOrderFileVersions,
    listOrderFiles,
    orderFileClientError,
    orderFileMimeType,
    presignOrderFile,
    restoreOrderFile
} from '../../api/order-files';

type PropsType = {
    orderId: number;
};

function formatBytes(bytes: number) {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploaderLabel(file: OrderFile) {
    return file.uploadedByUserName
        ? `${file.uploadedByTenantName}, ${file.uploadedByUserName}`
        : file.uploadedByTenantName;
}

function olderLabel(count: number) {
    return count === 1 ? '1 äldre version' : `${count} äldre versioner`;
}

function isImage(mimeType: string) {
    return mimeType === 'image/png' || mimeType === 'image/jpeg' || mimeType === 'image/jpg';
}

function isPdf(mimeType: string) {
    return mimeType === 'application/pdf';
}

const FILE_TITLE_PRESETS = ['Fraktsedel', 'POD'] as const;

const FILE_VISIBILITY = 'Filerna är bara synliga för ditt företag och den andra parten i uppdraget.';

function titleKindFrom(value: string) {
    if (value === 'Fraktsedel' || value === 'POD') {
        return value;
    }
    return value ? 'Annat' : '';
}

function FilePreview({ orderId, file }: { orderId: number; file: OrderFile }) {
    const previewable = isImage(file.mimeType) || isPdf(file.mimeType);
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!previewable) {
            return undefined;
        }
        let cancelled = false;
        downloadOrderFile(orderId, file.id, { inline: true })
            .then(({ presignedUrl }) => {
                if (!cancelled) {
                    setUrl(presignedUrl);
                }
            })
            .catch(() => {
                // The download action stays available when the preview URL cannot be created.
            });
        return () => {
            cancelled = true;
        };
    }, [file.id, orderId, previewable]);

    if (!previewable || !url) {
        return null;
    }

    if (isImage(file.mimeType)) {
        return (
            <Box
                component="img"
                src={url}
                alt={file.title}
                sx={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: 220,
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: 'action.hover'
                }}
            />
        );
    }

    return (
        <Box
            component="iframe"
            src={url}
            title={file.title}
            sx={{
                display: 'block',
                width: '100%',
                height: 260,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1
            }}
        />
    );
}

export default function OrderFiles({ orderId }: PropsType) {
    const userProfile = useSelector(selectUserProfile());
    const tenantId = userProfile?.tenantId;

    const [files, setFiles] = useState<OrderFile[]>([]);
    const [versions, setVersions] = useState<Record<string, OrderFile[]>>({});
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [titleKind, setTitleKind] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [replacing, setReplacing] = useState<OrderFile | null>(null);
    const [pendingDelete, setPendingDelete] = useState<OrderFile | null>(null);

    const load = useCallback(async () => {
        try {
            setIsLoading(true);
            setFiles(await listOrderFiles(orderId));
        } catch (error) {
            toastMessage(error, 'Filerna kunde inte hämtas.');
        } finally {
            setIsLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        load();
    }, [load]);

    const canMutate = (item: OrderFile) => tenantId != null && item.uploadedByTenantId === tenantId;

    const resetForm = () => {
        setUploadOpen(false);
        setTitleKind('');
        setTitle('');
        setDescription('');
        setFile(null);
        setFileInputKey((current) => current + 1);
        setReplacing(null);
    };

    const openUpload = () => {
        setReplacing(null);
        setTitleKind('');
        setTitle('');
        setDescription('');
        setFile(null);
        setFileInputKey((current) => current + 1);
        setUploadOpen(true);
    };

    const openDownload = async (fileId: number) => {
        const opened = window.open('', '_blank');
        try {
            const { presignedUrl } = await downloadOrderFile(orderId, fileId);
            if (opened) {
                opened.location.href = presignedUrl;
            } else {
                window.location.href = presignedUrl;
            }
        } catch (error) {
            opened?.close();
            toastMessage(error, 'Filen kunde inte laddas ner.');
        }
    };

    const toggleVersions = async (item: OrderFile) => {
        const isOpen = expanded[item.groupId];
        setExpanded((current) => ({ ...current, [item.groupId]: !isOpen }));
        if (isOpen || versions[item.groupId]) {
            return;
        }
        try {
            const rows = await listOrderFileVersions(orderId, item.groupId);
            setVersions((current) => ({ ...current, [item.groupId]: rows }));
        } catch (error) {
            toastMessage(error, 'Versionerna kunde inte hämtas.');
        }
    };

    const startReplace = (item: OrderFile) => {
        setReplacing(item);
        setTitleKind(titleKindFrom(item.title));
        setTitle(item.title);
        setDescription(item.description || '');
        setFile(null);
        setFileInputKey((current) => current + 1);
        setUploadOpen(true);
    };

    const onUpload = async () => {
        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            toastMessage('Rubrik krävs.');
            return;
        }
        if (!file) {
            toastMessage('Välj en fil.');
            return;
        }

        const wasReplace = !!replacing;
        setIsSaving(true);
        try {
            const prepared = await prepareOrderFile(file);
            const clientError = orderFileClientError(prepared);
            const mimeType = orderFileMimeType(prepared);
            if (clientError || !mimeType) {
                toastMessage(clientError || 'Filtypen stöds inte. Välj PDF, PNG, JPG, JPEG eller Office (doc, docx, xls, xlsx).');
                return;
            }
            const body = {
                mimeType,
                originalFilename: prepared.name,
                title: trimmedTitle,
                description: description.trim(),
                groupId: replacing?.groupId
            };
            const { presignedUrl, s3Key } = await presignOrderFile(orderId, body);
            await s3Upload({ file: prepared, presignedUrl, mimeType });
            await confirmOrderFile(orderId, { ...body, s3Key });
            resetForm();
            setVersions({});
            setExpanded({});
            await load();
            toastMessage(wasReplace ? 'Ny version uppladdad.' : 'Filen är uppladdad.');
        } catch (error) {
            const message = error instanceof Error ? error.message : '';
            toastMessage(message || error, 'Filen kunde inte laddas upp.');
        } finally {
            setIsSaving(false);
        }
    };

    const onDelete = async () => {
        if (!pendingDelete) {
            return;
        }
        setIsSaving(true);
        try {
            await deleteOrderFileGroup(orderId, pendingDelete.groupId);
            setPendingDelete(null);
            if (replacing?.groupId === pendingDelete.groupId) {
                resetForm();
            }
            await load();
            toastMessage('Filen är borttagen.');
        } catch (error) {
            toastMessage(error, 'Filen kunde inte tas bort.');
        } finally {
            setIsSaving(false);
        }
    };

    const onRestore = async (item: OrderFile) => {
        setIsSaving(true);
        try {
            await restoreOrderFile(orderId, item.id);
            setVersions({});
            setExpanded({});
            await load();
            toastMessage('Versionen är återställd.');
        } catch (error) {
            toastMessage(error, 'Versionen kunde inte återställas.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Block
                title="Filer"
                contentPadding
                infoTooltip={{
                    icon: (
                        <InfoOutlined
                            fontSize="small"
                            color="action"
                            tabIndex={0}
                            aria-label="Vem ser filerna"
                            sx={{ cursor: 'help' }}
                        />
                    ),
                    title: FILE_VISIBILITY
                }}
                ActionComponent={(
                    <Button
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={openUpload}
                        disabled={isSaving}
                    >
                        Ladda upp fil
                    </Button>
                )}
            >
                {isLoading && files.length === 0 && (
                    <Typography variant="body2">
                        Hämtar filer...
                    </Typography>
                )}
                {!isLoading && files.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Inga filer har laddats upp.
                    </Typography>
                )}
                {files.length > 0 && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(3, minmax(0, 1fr))'
                            },
                            gap: 2,
                            alignItems: 'start'
                        }}
                    >
                        {files.map((item) => (
                            <Block
                                key={item.id}
                                title={item.title}
                                subTitle={`${uploaderLabel(item)} · ${formatDateTime(item.createdAt)}`}
                                contentPadding
                                sx={{ bgcolor: COLOR_WHITEISH }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {item.deletedAt ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {`Filen har tagits bort · ${formatDateTime(item.deletedAt)}`}
                                        </Typography>
                                    ) : (
                                        <>
                                            <FilePreview orderId={orderId} file={item} />
                                            {item.description && (
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {item.description}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" color="text.secondary">
                                                {item.originalFilename}
                                                {' · '}
                                                {formatBytes(item.byteSize)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    startIcon={<Download />}
                                                    onClick={() => openDownload(item.id)}
                                                >
                                                    Ladda ner
                                                </Button>
                                                {item.olderVersionCount > 0 && (
                                                    <Button
                                                        size="small"
                                                        startIcon={expanded[item.groupId] ? <ExpandLess /> : <ExpandMore />}
                                                        onClick={() => toggleVersions(item)}
                                                    >
                                                        {olderLabel(item.olderVersionCount)}
                                                    </Button>
                                                )}
                                                {canMutate(item) && (
                                                    <>
                                                        <Button size="small" onClick={() => startReplace(item)} disabled={isSaving}>
                                                            Ersätt
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            startIcon={<Delete />}
                                                            onClick={() => setPendingDelete(item)}
                                                            disabled={isSaving}
                                                        >
                                                            Ta bort
                                                        </Button>
                                                    </>
                                                )}
                                            </Box>
                                            {expanded[item.groupId] && (
                                                <Box sx={{ pl: 1 }}>
                                                    {(versions[item.groupId] || []).map((older) => (
                                                        <Box key={older.id} sx={{ py: 1 }}>
                                                            <Typography variant="body2">
                                                                {`Version ${older.version}: ${older.title}`}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {uploaderLabel(older)}
                                                                {' · '}
                                                                {formatDateTime(older.createdAt)}
                                                                {' · '}
                                                                {older.originalFilename}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<Download />}
                                                                    onClick={() => openDownload(older.id)}
                                                                >
                                                                    Ladda ner
                                                                </Button>
                                                                {canMutate(item) && (
                                                                    <Button
                                                                        size="small"
                                                                        startIcon={<Restore />}
                                                                        onClick={() => onRestore(older)}
                                                                        disabled={isSaving}
                                                                    >
                                                                        Återställ
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                    {!versions[item.groupId] && (
                                                        <Typography variant="body2">Hämtar versioner...</Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Block>
                        ))}
                    </Box>
                )}
            </Block>

            <Popup
                open={uploadOpen}
                title={replacing ? `Ersätt "${replacing.title}"` : 'Ladda upp fil'}
                okLabel={replacing ? 'Ladda upp ny version' : 'Ladda upp'}
                handleOk={onUpload}
                handleClose={resetForm}
                isSubmitting={isSaving}
                okDisabled={!title.trim() || !file}
                fullWidth
                maxWidth="sm"
                disableEnforceFocus
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {FILE_VISIBILITY}
                    </Typography>
                    <FormControl fullWidth size="small" required disabled={isSaving}>
                        <InputLabel id="order-file-title-label">Rubrik</InputLabel>
                        <Select
                            labelId="order-file-title-label"
                            label="Rubrik"
                            value={titleKind}
                            onChange={(event) => {
                                const kind = event.target.value;
                                setTitleKind(kind);
                                if (kind === 'Annat') {
                                    setTitle(titleKind === 'Annat' ? title : '');
                                } else {
                                    setTitle(kind);
                                }
                            }}
                        >
                            <MenuItem value="" sx={{ display: 'none' }} />
                            {FILE_TITLE_PRESETS.map((preset) => (
                                <MenuItem key={preset} value={preset}>{preset}</MenuItem>
                            ))}
                            <MenuItem value="Annat">Annat</MenuItem>
                        </Select>
                    </FormControl>
                    {titleKind === 'Annat' && (
                        <TextField
                            label="Egen rubrik"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            required
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 200 }}
                            disabled={isSaving}
                        />
                    )}
                    <TextField
                        label="Beskrivning"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        inputProps={{ maxLength: 2000 }}
                        disabled={isSaving}
                    />
                    <Box>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isSaving}
                            sx={{ position: 'relative' }}
                        >
                            Välj fil
                            <input
                                key={fileInputKey}
                                type="file"
                                accept={ORDER_FILE_ACCEPT}
                                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    opacity: 0,
                                    width: '100%',
                                    height: '100%',
                                    fontSize: '16px'
                                }}
                            />
                        </Button>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {file ? file.name : 'Ingen fil vald'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            PDF, PNG, JPG, JPEG eller Office (doc, docx, xls, xlsx). Max 25 MB.
                        </Typography>
                    </Box>
                </Box>
            </Popup>

            <Popup
                open={!!pendingDelete}
                title="Ta bort fil"
                body="Filen döljs för båda parter. Den ligger kvar i lagringen tills support tar bort den."
                deleteLabel="Ta bort"
                handleDelete={onDelete}
                handleClose={() => setPendingDelete(null)}
                isSubmitting={isSaving}
            />
        </>
    );
}
